import type { JSX } from 'react';
import { useState } from 'react';
import { ANDROID_APP_URL, IOS_APP_URL } from '../../config';
import {
  acceptPatientInvite,
  MIN_PASSWORD_LENGTH,
  PublicError,
  resetPatientPassword,
  takeTokenFromUrl,
} from '../../data/public';
import { FormError, NewPassword, passwordProblem, PublicCard } from './PublicCard';

/**
 * `/welcome?token=…` — a patient's invitation.
 *
 * The patient chooses a password here, then installs the phone app and signs
 * in with it. Redeeming in the browser keeps the app simple — it needs a
 * sign-in screen and nothing else — and keeps the token out of app links,
 * where getting it from an email into an app reliably is its own project.
 *
 * Written for somebody who is fatigued, on a phone, and may not be confident
 * with either: short sentences, one thing per step, no jargon. And nothing
 * about the condition — see `fatigue_api/mail.py` for why the invitation never
 * says what it is for.
 *
 * `/reset?token=…` is the same page in `reset` mode: a link a clinician sent so
 * an existing patient can choose a new password. Same form, different words,
 * and it ends at "sign in again" rather than "get the app".
 */
export function Welcome({ mode = 'invite' }: { readonly mode?: 'invite' | 'reset' }): JSX.Element {
  const resetting = mode === 'reset';
  const [token] = useState(takeTokenFromUrl);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (token === null || busy) return;
    const problem = passwordProblem(password, confirm, MIN_PASSWORD_LENGTH);
    if (problem !== null) {
      setError(problem);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      if (resetting) await resetPatientPassword({ token, password });
      else await acceptPatientInvite({ token, password });
      setDone(true);
    } catch (err) {
      setError(err instanceof PublicError ? err.message : 'Something went wrong. Try again.');
      setBusy(false);
    }
  }

  if (done && resetting) {
    return (
      <PublicCard subtitle="Password changed">
        <p className="invite__preamble">
          Your new password is saved. The app has been signed out on your phone. Open it and sign
          in again with your email address and the new password. Your entries are all still there.
        </p>
      </PublicCard>
    );
  }

  if (done) {
    return (
      <PublicCard subtitle="You are all set up">
        <p className="invite__preamble">
          Your password is saved. Now get the app on your phone. Sign in with the email address
          this invitation was sent to, and the password you just chose.
        </p>
        <StoreLinks />
      </PublicCard>
    );
  }

  if (token === null) {
    return (
      <PublicCard subtitle={resetting ? 'Choose a new password' : 'Welcome'}>
        <p className="invite__preamble">
          This link is incomplete. Open the link from your email again. If it still does not work,
          ask your care team to send a new one.
        </p>
        <StoreLinks />
      </PublicCard>
    );
  }

  return (
    <PublicCard subtitle={resetting ? 'Choose a new password' : 'Welcome'}>
      <form onSubmit={submit}>
        <FormError
          message={
            error === null
              ? null
              : resetting
                ? `${error} Each link works once, for seven days. If this one has been used or has run out, ask your care team to send another.`
                : `${error} If you have already chosen a password, you are set up — just get the app and sign in. Otherwise ask your care team for a new link.`
          }
        />
        <p className="invite__preamble">
          {resetting
            ? 'Your care team sent you this link so you can choose a new password. Afterwards, sign in to the app on your phone with it.'
            : 'Your care team has set up an account for you. Choose a password to finish. You will use it to sign in to the app on your phone, with the email address this invitation was sent to.'}
        </p>
        <NewPassword
          password={password}
          confirm={confirm}
          onPassword={setPassword}
          onConfirm={setConfirm}
          minLength={MIN_PASSWORD_LENGTH}
        />
        <button type="submit" className="primary signin__submit" disabled={busy}>
          {busy ? 'Saving…' : 'Save my password'}
        </button>
      </form>
    </PublicCard>
  );
}

/** Store buttons once the app is published; until then, who to ask instead. */
function StoreLinks(): JSX.Element {
  if (IOS_APP_URL === undefined && ANDROID_APP_URL === undefined) {
    return (
      <p className="signin__note">
        The app is not in the App Store or Google Play yet. Your care team will help you install
        it.
      </p>
    );
  }
  return (
    <div className="welcome__stores">
      {IOS_APP_URL !== undefined && (
        <a className="primary signin__submit" href={IOS_APP_URL}>
          Get it on the App Store
        </a>
      )}
      {ANDROID_APP_URL !== undefined && (
        <a className="primary signin__submit" href={ANDROID_APP_URL}>
          Get it on Google Play
        </a>
      )}
    </div>
  );
}
